import { ControlledReadField, ControlledTextField, ControlNumberField } from 'components/Form';
import { ReadField } from 'components/ReadField';
import type { BimContractOtherStatusEnum, CatalogOtherCostTypeData } from 'gql/graphql';
import {
    ChapterEnum,
    ContractOtherStatusEnum,
    OtherCostCalculationTypeEnum,
    ProjectMethodologyEnum,
} from 'gql/graphql';
import type { TypeItem } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import { OTHER_SECTION_OR_CHAPTER, TYPE_VARIANTS } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import type { IUpdateOtherCostCanEditCommon } from 'pages/Project/Common/Contract/OtherEdit/hooks/useUerUpdateContractOtherCostCanEdit';
import { UpdateOtherCostEditable } from 'pages/Project/Common/Contract/OtherEdit/hooks/useUerUpdateContractOtherCostCanEdit';
import { Grid, GridItem, RadioGroup } from 'uikit';
import { formatNumberToRu } from 'utils';

import React, { useCallback, useMemo } from 'react';
import type { Control } from 'react-hook-form';

import { OtherCostTypeSelect, otherCostTypeSelectLabel } from './OtherCostTypeSelect';

import Styles from './EditOtherForm.module.css';

const getItemLabel = (item: { label: any }) => item.label;

export const getOtherMethod = ({
    status,
    isGlobal,
}: {
    status?: BimContractOtherStatusEnum | ContractOtherStatusEnum;
    isGlobal?: boolean;
}) => {
    if (status && [ContractOtherStatusEnum.NewRecord, 'DRAFT'].includes(status) && !isGlobal) {
        return 'Вручную';
    }

    return isGlobal ? 'Из справочника методик расчета' : 'Из справочника договора';
};

interface IEditOtherCommon {
    control: Control<any>;
    isGlobal?: boolean;
    chapter: any;
    selectedType: TypeItem;
    selectedChapterOrSection: any;
    changeSelectedTypeHandler: (newType: TypeItem) => void;
    changeSelectedChapterOrSection: (value: string) => void;
    globalOtherName?: string;
    isTooltipHidden?: boolean;
    otherStatus?: BimContractOtherStatusEnum | ContractOtherStatusEnum;
    methodology: ProjectMethodologyEnum;
    calculatingTypeValue?: number;
    otherId: number;
    editConfig?: IUpdateOtherCostCanEditCommon;
}

export const EditOtherCommon = (props: IEditOtherCommon) => {
    const {
        control,
        isGlobal,
        chapter,
        selectedType,
        changeSelectedTypeHandler,
        globalOtherName,
        selectedChapterOrSection,
        changeSelectedChapterOrSection,
        isTooltipHidden,
        otherStatus,
        methodology,
        calculatingTypeValue,
        otherId,
        editConfig,
    } = props;

    const isUer = useMemo(() => methodology === ProjectMethodologyEnum.Uer, [methodology]);

    const methodValue = getOtherMethod({ isGlobal, status: otherStatus });
    const chapterValue = chapter === ChapterEnum.Chapter9 ? 'Глава 9' : 'Раздел "Лимиты Заказчика"';

    const handleChangeType = useCallback(({ value }) => changeSelectedTypeHandler(value), [changeSelectedTypeHandler]);
    const onSelectedChapterOrSectionChange = useCallback(
        ({ value }) => changeSelectedChapterOrSection(value),
        [changeSelectedChapterOrSection],
    );

    const canEdit = useMemo(() => editConfig?.[UpdateOtherCostEditable.canEdit], [editConfig]);
    const canEditSection = useMemo(() => editConfig?.[UpdateOtherCostEditable.section], [editConfig]);

    return (
        <Grid>
            {!canEdit && (
                <GridItem className={`${Styles.negativeM}`}>
                    <ReadField value={methodValue} label="Способ создания" />
                </GridItem>
            )}
            {canEdit && editConfig?.[UpdateOtherCostEditable.name] ? (
                <GridItem className={Styles.marginBlock}>
                    <ControlledTextField
                        name="name"
                        label="Наименование"
                        required
                        control={control}
                        type="textarea"
                        rules={{
                            required: 'Обязательное поле',
                            maxLength: {
                                value: 255,
                                message: 'Максимальное количество символов – 255',
                            },
                        }}
                        isTooltipHidden={isTooltipHidden}
                    />
                </GridItem>
            ) : (
                <GridItem className={Styles.negativeM}>
                    <ReadField value={globalOtherName} label="Наименование" />
                </GridItem>
            )}

            <GridItem className={Styles.marginBlock}>
                <ControlledTextField
                    name="key"
                    label="Код"
                    placeholder="Введите"
                    type="text"
                    control={control}
                    isTooltipHidden={isTooltipHidden}
                    disabled={!editConfig?.[UpdateOtherCostEditable.key]}
                />
            </GridItem>

            {canEdit && editConfig?.[UpdateOtherCostEditable.calculatingType] ? (
                <GridItem className={Styles.marginBlock}>
                    <div className="TextField TextField_labelPosition_left TextField_size_s TextField_view_default TextField_width_full">
                        <div className="Text Text_lineHeight_m Text_size_s Text_view_secondary FieldLabel TextField-Label TextField-Label_labelPosition_left">
                            Способ расчета
                        </div>

                        <RadioGroup
                            name="type"
                            direction="row"
                            items={TYPE_VARIANTS}
                            value={selectedType}
                            className={Styles.RadioGroup}
                            getItemLabel={getItemLabel}
                            onChange={handleChangeType}
                        />
                    </div>
                </GridItem>
            ) : (
                <GridItem className={Styles.negativeM}>
                    <ReadField value={selectedType.label} label="Способ расчета" />
                </GridItem>
            )}

            {selectedType.id === OtherCostCalculationTypeEnum.Percent &&
                (canEdit && editConfig?.[UpdateOtherCostEditable.calculatingTypeValue] ? (
                    <GridItem className={Styles.marginBlock}>
                        <ControlNumberField
                            name="calculatingTypeValue"
                            label="Величина %"
                            labelPosition="left"
                            control={control}
                            type="text"
                            size="s"
                            required
                            rules={{ required: 'Обязательное поле' }}
                            className={Styles.typeValueField}
                            isTooltipHidden={isTooltipHidden}
                            decimalScale={isUer ? 2 : 3}
                        />
                    </GridItem>
                ) : (
                    <GridItem className={Styles.negativeM}>
                        <ReadField value={formatNumberToRu(calculatingTypeValue, isUer ? 2 : 3)} label="Величина %" />
                    </GridItem>
                ))}

            {!canEditSection && (
                <GridItem className={`${Styles.marginTop} ${Styles.negativeM}`}>
                    <ReadField value={chapterValue} label="Раздел" />
                </GridItem>
            )}

            {canEditSection && (
                <GridItem className={Styles.marginBlock}>
                    <div className="TextField TextField_labelPosition_left TextField_size_s TextField_view_default TextField_width_full">
                        <div className="Text Text_lineHeight_m Text_size_s Text_view_secondary FieldLabel TextField-Label TextField-Label_labelPosition_left">
                            Раздел
                        </div>
                        <RadioGroup
                            name="selectedChapterOrSection"
                            direction="row"
                            items={OTHER_SECTION_OR_CHAPTER}
                            value={selectedChapterOrSection}
                            getItemLabel={getItemLabel}
                            onChange={onSelectedChapterOrSectionChange}
                            className={Styles.RadioGroup}
                        />
                    </div>
                </GridItem>
            )}

            {!canEdit || !editConfig?.[UpdateOtherCostEditable.type] ? (
                <GridItem className={Styles.negativeM}>
                    <ControlledReadField
                        control={control}
                        label="Тип прочих"
                        name="otherCostType"
                        labelRightSide={
                            <div className={Styles.correctionLabelMargin}>{otherCostTypeSelectLabel()}</div>
                        }
                        formatter={(value: CatalogOtherCostTypeData) => value?.name}
                    />
                </GridItem>
            ) : (
                <OtherCostTypeSelect
                    isTooltipHidden={isTooltipHidden}
                    isDisabled={!canEdit || !editConfig?.[UpdateOtherCostEditable.type]}
                    control={control}
                    id={otherId}
                />
            )}
        </Grid>
    );
};

EditOtherCommon.displayName = 'EditOtherCommon';
